import { browser } from "$app/env";
import { QueryClient } from "@tanstack/svelte-query";
import type { DataTag, QueryKey } from "@tanstack/svelte-query";
import { UNREACHABLE } from "@colossus/api-client";
import type { ApiFailure, ApiResult, Fetch, TenantPaths, paths } from "@colossus/api-client";
import { ApiError, appClient, consoleClient } from "./api.ts";
import type { Read } from "./read.ts";
import { perReader, reader } from "./reader.ts";

export const queryClient = perReader(
  () =>
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: browser ? 0 : Infinity,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          gcTime: browser ? 30 * 60_000 : Infinity,
        },
        mutations: { retry: false },
      },
    }),
);

export const HELD = { staleTime: Infinity } as const;

type ConsolePaths = Omit<paths, keyof TenantPaths>;

type GetPaths<Paths> = {
  [P in keyof Paths]: Paths[P] extends { get: object } ? P : never;
}[keyof Paths];

type GetOf<Paths, P extends keyof Paths> = Paths[P] extends { get: infer O } ? O : never;

type QueryOf<O> = O extends { parameters: { query?: infer Q } } ? Q : undefined;

type PathOf<O> = O extends { parameters: { path?: infer P } } ? P : undefined;

type OkOf<O> = O extends { responses: { 200: { content: { "application/json": infer R } } } }
  ? R
  : never;

export type AppGet = GetPaths<TenantPaths>;

export type ConsoleGet = GetPaths<ConsolePaths>;

export interface AppRequest<P extends AppGet> {
  readonly query?: QueryOf<GetOf<TenantPaths, P>>;
  readonly path?: Omit<NonNullable<PathOf<GetOf<TenantPaths, P>>>, "tenantId">;
}

interface ConsoleRequest<P extends ConsoleGet> {
  readonly query?: QueryOf<GetOf<ConsolePaths, P>>;
  readonly path?: PathOf<GetOf<ConsolePaths, P>>;
}

export type AppOk<P extends AppGet> = OkOf<GetOf<TenantPaths, P>>;

export type ConsoleOk<P extends ConsoleGet> = OkOf<GetOf<ConsolePaths, P>>;

export const appKey = <P extends AppGet>(
  tenantId: string,
  path: P,
  request: AppRequest<P> = {},
): readonly ["app", string, P, AppRequest<P>] => ["app", tenantId, path, request];

export const consoleKey = <P extends ConsoleGet>(
  path: P,
  request: ConsoleRequest<P> = {},
): readonly ["console", P, ConsoleRequest<P>] => ["console", path, request];

export const fetchNow: Fetch = (input, init) => reader().fetch(input, init);

const landed = <T>(path: string, result: ApiResult<T>): T => {
  if (result.ok) return result.data;
  throw new ApiError(path, result);
};

const readOf = <Key extends QueryKey, T>(queryKey: Key, queryFn: () => Promise<T>) => ({
  queryKey: queryKey as DataTag<Key, T, Error>,
  queryFn,
});

export const appRead = <P extends AppGet>(tenantId: string, path: P, request: AppRequest<P> = {}) =>
  readOf(appKey(tenantId, path, request), async (): Promise<AppOk<P>> =>
    landed(
      path,
      (await appClient(tenantId, fetchNow).call(
        "get",
        path as never,
        request as never,
      )) as ApiResult<AppOk<P>>,
    ),
  );

export const consoleRead = <P extends ConsoleGet>(path: P, request: ConsoleRequest<P> = {}) =>
  readOf(consoleKey(path, request), async (): Promise<ConsoleOk<P>> =>
    landed(
      path,
      (await consoleClient(fetchNow).call("get", path as never, request as never)) as ApiResult<
        ConsoleOk<P>
      >,
    ),
  );

export const failureOf = (error: unknown): ApiFailure | null =>
  error instanceof ApiError ? error.failure : null;

export const answerOf = <T>(reading: Promise<T>): Promise<ApiResult<T>> =>
  reading.then(
    (data) => ({ ok: true as const, status: 200, data }),
    (error: unknown) => {
      const failure = failureOf(error);
      if (failure === null) throw error;
      return failure;
    },
  );

export const opening = (read: {
  readonly queryKey: QueryKey;
  readonly queryFn: () => Promise<unknown>;
  readonly staleTime?: number;
}): Promise<void> => {
  const held = queryClient().getQueryState(read.queryKey)?.status === "success";
  const reading = queryClient()
    .fetchQuery({ staleTime: 0, ...read })
    .then(
      () => undefined,
      () => undefined,
    );
  return held ? Promise.resolve() : reading;
};

export const invalidated = (...keys: readonly QueryKey[]): Promise<unknown> =>
  Promise.all(keys.map((queryKey) => queryClient().invalidateQueries({ queryKey })));

export const shownRead = <T>(query: {
  readonly isSuccess: boolean;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly data: T | undefined;
}): Read<T> =>
  query.isSuccess ? query.data : query.isError && !query.isFetching ? null : undefined;

interface Landed<T> {
  readonly isSuccess: boolean;
  readonly isError: boolean;
  readonly data: T | undefined;
  readonly error: unknown;
}

export const landedRead = <T>(enabled: boolean, read: Landed<T>): T | null =>
  enabled && read.isSuccess && read.data !== undefined ? read.data : null;

export const refusalStatus = (enabled: boolean, read: Landed<unknown>): number | null =>
  !enabled || read.isSuccess || !read.isError
    ? null
    : (failureOf(read.error)?.status ?? UNREACHABLE);
